/**
 * Video action helpers — DRY for video entity actions (Hono).
 */

import type { Context } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import { checkFileAccess } from './actions/helpers.js'
import { getStorageService, type ResolvedFile } from './storage.service.js'
import { getVideoProcessingService, isVideoMimetype } from './video-processing.service.js'

export interface VideoFileRecord {
  id: string
  filename: string
  mimetype: string
  size: number
  visibility: string
  hash?: string
  created_by?: string | null
}

/**
 * Validates access, checks video mimetype, checks ffmpeg, resolves to local path.
 * Caller MUST call resolved.cleanup() in finally.
 */
export async function resolveVideoFile(
  file: VideoFileRecord,
  c: Context<any>,
  ctx: ModuleContext
): Promise<ResolvedFile> {
  checkFileAccess(file, c, ctx)

  if (!isVideoMimetype(file.mimetype)) {
    throw new ctx.core.errors.ValidationError('Not a video file')
  }

  const videoService = getVideoProcessingService()
  if (!await videoService.isAvailable()) {
    throw new ctx.core.errors.AppError('Video processing not available (ffmpeg required)', 503)
  }

  return getStorageService().resolveInputFile(file.id)
}

/**
 * Applies standard headers for video processing results to a Hono context.
 */
export function setVideoResultHeaders(
  c: Context<any>,
  file: VideoFileRecord,
  result: { buffer: Buffer; mimetype: string; format: string },
  etag?: string
): void {
  c.header('Content-Type', result.mimetype)
  c.header(
    'Content-Disposition',
    `inline; filename="${encodeURIComponent(file.filename.replace(/\.[^.]+$/, '.' + result.format))}"`
  )
  c.header('Content-Length', result.buffer.length.toString())
  c.header('Cache-Control', 'public, max-age=86400')
  if (etag) c.header('ETag', etag)
}
